import React, { useEffect, useRef, useState } from "react";
import styles from "./sidebar-nav-menu.module.scss";
import type { TuvisIconNamesType } from "@whatslly/types/UI-V2/BaseElements/Svg/TuvisIconNamesType";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { NavLink, useLocation } from "react-router-dom";
import classnames from "classnames";
import { NavButton } from "@whatslly/ui-components/NavButton";
import { IconType } from "react-icons";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { TuvisSkeleton } from "../TuvisSkeleton";

export type NavItemType = {
  testId?: string;
  to: string;
  end?: boolean;
  title: string;
  hidden?: boolean;
  iconName?: TuvisIconNamesType;
  icon?: IconType;
  category?: string;
  areChildrenLoading?: () => boolean;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
  sideComponent?: React.JSX.Element;
  separatedSection?: boolean;
};

const undefinedCategory = "undefined";
export const SideBarNavMenu: React.FC<{
  title?: React.ReactNode;
  menuItems: NavItemType[];
  userNavButton?: boolean;
  minimized?: boolean;
  subMenu?: boolean;
  isLoading?: boolean;
}> = ({ title, menuItems, userNavButton, minimized, subMenu, isLoading }) => {
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);
  const [isInitialized, setIsInitialized] = useState<boolean>(!subMenu);
  const [isMinimized, setIsMinimized] = useState<boolean>((lastPath.current ?? "" === pathname) ? (minimized ?? false) : !minimized);
  const { t } = useTranslation();
  const groupedItems = React.useMemo(() => {
    const grouped: Record<string, NavItemType[]> = {};

    const uniqueCategories = new Set(menuItems.filter((item) => item.category).map((item) => item.category));
    uniqueCategories.forEach((category) => {
      grouped[category!] = [];
    });
    grouped[undefinedCategory] = [];
    menuItems.forEach((item) => {
      if (item.hidden) return;

      const category = item.category || undefinedCategory;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });

    return grouped;
  }, [menuItems]);

  useEffect(() => {
    setIsInitialized(true);
    lastPath.current = pathname;
    // we use this to prevent the menu from flickering when the page is loaded
    const timer = setTimeout(() => {
      setIsMinimized(minimized ?? false);
    }, 50);

    return () => clearTimeout(timer);
  }, [minimized, pathname]);

  return (
    <div
      className={classnames(styles.navMenu, {
        [styles.withoutTitle]: !title,
        [styles.submenu]: subMenu,
        [styles.init]: isInitialized,
        [styles.loading]: isLoading,
      })}
      data-minimized={isMinimized}
    >
      {title && <h2>{title}</h2>}
      {Object.entries(groupedItems).map(([category, items]) => {
        if (items.length === 0) return null;
        const isCategoryHidden = category === undefinedCategory || t(`menu.category.${category}`) === `menu.category.${category}`;
        const isSeparatedSection = items.some((item) => item.separatedSection);

        const content = (
          <React.Fragment key={category}>
            {!isCategoryHidden && (
              <TuvisSkeleton
                width={"120px"}
                height={isMinimized ? "10px" : "12px"}
                loading={isLoading}
                className={classnames(styles.categoryLabel, { [styles.loading]: isLoading })}
              >
                <div className={styles.categoryLabel}>
                  <span>{t(`menu.category.${category}`)}</span>
                </div>
              </TuvisSkeleton>
            )}
            {items.map((item, index) => (
              <NavItem key={index} item={item} index={index} userNavButton={userNavButton} isMinimized={isMinimized} isLoading={isLoading} />
            ))}
          </React.Fragment>
        );

        if (isSeparatedSection) {
          return (
            <div key={category} className={styles.separatedSection}>
              {content}
            </div>
          );
        }

        return content;
      })}
    </div>
  );
};

const NavItem = ({
  item,
  index,
  userNavButton,
  isMinimized,
  isLoading,
}: {
  item: NavItemType;
  index: number;
  isMinimized: boolean;
  userNavButton?: boolean;
  isLoading?: boolean;
}) => {
  if (userNavButton) {
    return (
      <NavButton
        key={index}
        data-testid={item.testId}
        to={item.to}
        className={({ isActive }) => classnames(styles.navLink, { [styles.active]: isActive })}
      >
        <div className={styles.navItemIcon}>
          {item.icon ? item.icon({}) : item.iconName && <SvgV2 className={styles.logo} iconName={item.iconName} />}
        </div>
        <span className={styles.navItemText}>{item.title}</span>
      </NavButton>
    );
  }

  const shouldShowDisabledTooltip = Boolean(item.disabled && item.disabledTooltip && !isLoading);
  const navLink = (
    <NavLink
      key={index}
      data-testid={item.testId}
      to={item.to}
      end={item.end}
      onClick={(e) => {
        if (item.disabled || isLoading) e.preventDefault();
      }}
      className={({ isActive }) =>
        classnames(styles.navLink, {
          [styles.active]: isActive && !isLoading,
          [styles.disabled]: item.disabled || isLoading,
        })
      }
    >
      <TuvisSkeleton
        width={isMinimized ? "36px" : "100%"}
        height={isMinimized ? "36px" : "34px"}
        className={styles.navItemSkeleton}
        loading={isLoading}
      >
        {(item.icon || item.iconName) && (
          <div className={styles.navItemIcon}>
            {!isMinimized ? (
              item.icon ? (
                item.icon({})
              ) : (
                item.iconName && <SvgV2 className={styles.logo} iconName={item.iconName} />
              )
            ) : (
              <Tooltip
                value={shouldShowDisabledTooltip ? item.disabledTooltip : item.title}
                placement={"right"}
                className={shouldShowDisabledTooltip ? styles.disabledTooltip : styles.tooltip}
                lineWrap={shouldShowDisabledTooltip}
                disabled={isLoading}
              >
                <div>{item.icon ? item.icon({}) : item.iconName && <SvgV2 className={styles.logo} iconName={item.iconName} />}</div>
              </Tooltip>
            )}
          </div>
        )}
        <span className={styles.navItemText}>{item.title}</span>
        {item.sideComponent && <div className={styles.customActionWrapper}>{item.sideComponent}</div>}
      </TuvisSkeleton>
    </NavLink>
  );

  if (shouldShowDisabledTooltip && !isMinimized) {
    return (
      <Tooltip value={item.disabledTooltip} placement={"right"} className={styles.disabledTooltip} lineWrap={true} disabled={isLoading}>
        {navLink}
      </Tooltip>
    );
  }

  return navLink;
};
